import React, { useEffect, useState } from "react";
import { Column } from "primereact/column";
import { TreeTable } from "primereact/treetable";
import { classNames } from "primereact/utils";
import { convertKBtoGo } from "../../utils/convertiseur";
import { Actif, Classeur, Fichier, User } from "../../interfaces";
import moment from "moment";
import { Chip } from "primereact/chip";

interface ArborescenceProps {
  actif: Actif;
  searchQuery: string;
  UserQuery: User;
  selectedClasseur: Classeur[];
  setSelectedClasseur: any;
  selectionRange: any;
  selectedDossiers: string[];
  selectedEntite?: any[]; // tableau des bâtiments/niveaux/espaces/pièces sélectionnés (multi-sélection)
  selectedEspaceId?: number | null; // filtre "Espace" (sélection unique) — id de l'espace réel du bien
}

export const ArborescenceGexpertise = ({
  actif,
  searchQuery,
  UserQuery,
  selectedClasseur,
  setSelectedClasseur,
  selectionRange,
  selectedDossiers,
  selectedEntite,
  selectedEspaceId,
}: ArborescenceProps) => {
  const [expandedKeys, setExpandedKeys] = useState({});
  const [mergedData, setMergedData] = useState([]);

  const [data, setData] = useState([
    {
      key: "1",
      label: "Propriété & Foncier",
      children: [
        { key: "1-10", label: "Titres & actes" },
        { key: "1-11", label: "Estimations & valeurs" },
        { key: "1-12", label: "Plans & bornage" },
        { key: "1-13", label: "Servitudes" },
        { key: "1-14", label: "Publicité & Foncier" },
        { key: "1-15", label: "Projets & acquisitions" },
      ],
    },
    {
      key: "2",
      label: "Urbanisme & Planification",
      children: [
        { key: "2-20", label: "Documents réglementaires" },
        { key: "2-21", label: "Permis & autorisations" },
        { key: "2-22", label: "Suivi de chantier" },
      ],
    },
    {
      key: "3",
      label: "Copropriété & Associations",
      children: [
        { key: "3-30", label: "Assemblées générales" },
        { key: "3-31", label: "Correspondances" },
        { key: "3-32", label: "Réglementation" },
        { key: "3-33", label: "Note de service" },
        { key: "3-34", label: "Vie collective & services" },
      ],
    },
    {
      key: "4",
      label: "Gestion locative",
      children: [
        { key: "4-40", label: "Contrat" },
        { key: "4-41", label: "Etats des lieux" },
        { key: "4-42", label: "Loyers & charges" },
      ],
    },
    { key: "5", label: "Fiscalité", children: [{ key: "5-50", label: "Fiscalité" }] },
    { key: "6", label: "Financement", children: [{ key: "6-60", label: "Financement" }] },
    {
      key: "7",
      label: "Assurances",
      children: [
        { key: "7-70", label: "Contrats" },
        { key: "7-71", label: "Sinistres" },
      ],
    },
    {
      key: "8",
      label: "Travaux & Entretien",
      children: [
        { key: "8-80", label: "Etudes & estimations" },
        { key: "8-81", label: "Devis" },
        { key: "8-82", label: "Contrat" },
        { key: "8-83", label: "Factures & honoraires" },
        { key: "8-84", label: "Bordereau de livraison" },
        { key: "8-85", label: "Fiche d'intervention" },
        { key: "8-86", label: "Demande d'intervention" },
        { key: "8-87", label: "Réception & garanties" },
      ],
    },
    {
      key: "9",
      label: "Diagnostics & Etudes techniques",
      children: [
        {
          key: "9-90",
          label: "Diagnostics obligatoires",
          children: [
            {
              key: "9-90-1",
              label: "Amiante",
              children: [
                {
                  key: "9-90-1-10",
                  label: "Dossier amiante",
                  children: [
                    { key: "9-90-1-10-11", label: "Rapport de diagnostic" },
                    { key: "9-90-1-10-12", label: "Fiche récapitulative" },
                    { key: "9-90-1-10-13", label: "Annexes" },
                  ],
                },
              ],
            },
            { key: "9-90-2", label: "DPE" },
            { key: "9-90-3", label: "Plomb" },
          ],
        },
        { key: "9-91", label: "Sécurité & risques" },
        { key: "9-92", label: "Accessibilité" },
        { key: "9-93", label: "Etudes de sols" },
      ],
    },
    { key: "10", label: "Plans & Techniques", children: [{ key: "10-100", label: "Plans & Techniques" }] },
    { key: "11", label: "Litiges & Contentieux", children: [{ key: "11-110", label: "Litiges & contentieux" }] },
    { key: "12", label: "Gouvernance", children: [{ key: "12-120", label: "Conseil d'administration" }] },
  ]);

  const filterTree = (data, query) => {
    const lowerQuery = query.toLowerCase();

    const filterNode = (node) => {
      const matchesSelf = node.label && node.label.toLowerCase().includes(lowerQuery);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children.map(filterNode).filter((child) => child !== null);
      }

      const hasMatchingChild = filteredChildren.length > 0;

      if (matchesSelf || hasMatchingChild) {
        return {
          ...node,
          children: matchesSelf ? node.children : filteredChildren,
        };
      }

      return null;
    };

    return data.map(filterNode).filter((node) => node !== null);
  };

  // Filtre par dossier(s) sélectionné(s) : matche le label exact du nœud
  // (ex: "DPE", "Amiante") et conserve tout son sous-arbre (classeurs + fichiers).
  const filterByDossiers = (data, dossierNames: string[]) => {
    const filterNode = (node) => {
      const matchesSelf = node.label && dossierNames.includes(node.label);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children.map(filterNode).filter((child) => child !== null);
      }

      const hasMatchingChild = filteredChildren.length > 0;

      if (matchesSelf || hasMatchingChild) {
        return {
          ...node,
          children: matchesSelf ? node.children : filteredChildren,
        };
      }

      return null;
    };

    return data.map(filterNode).filter((node) => node !== null);
  };

  // Filtre par espace sélectionné (sélection unique) : ne garde que les
  // classeurs rattachés à cet espace, et conserve tout leur sous-arbre (fichiers).
  const filterByEspace = (data, espaceId: number) => {
    const filterNode = (node) => {
      const matchesSelf =
        node.isClasseur && node.espaces?.some((espace) => espace.id === espaceId);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children.map(filterNode).filter((child) => child !== null);
      }

      const hasMatchingChild = filteredChildren.length > 0;

      if (matchesSelf || hasMatchingChild) {
        return {
          ...node,
          children: matchesSelf ? node.children : filteredChildren,
        };
      }

      return null;
    };

    return data.map(filterNode).filter((node) => node !== null);
  };

  const getAllKeysExpanded = (nodes, keys = {}) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        keys[node.key] = true;
        getAllKeysExpanded(node.children, keys);
      }
    });
    return keys;
  };

  // Vérifie si un nœud (ou un de ses descendants) est un classeur réel
  const nodeHasClasseur = (node): boolean => {
    if (node.isClasseur) return true;
    if (node.children && node.children.length > 0) {
      return node.children.some(nodeHasClasseur);
    }
    return false;
  };

  // N'ouvre que les branches qui mènent réellement à un classeur
  // (contrairement à getAllKeysExpanded qui ouvre tout, même les
  // catégories statiques vides pour l'entité sélectionnée)
  const getKeysExpandedForClasseurs = (nodes, keys = {}) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0 && nodeHasClasseur(node)) {
        keys[node.key] = true;
        getKeysExpandedForClasseurs(node.children, keys);
      }
    });
    return keys;
  };

  const hasSelectedEntite = selectedEntite && selectedEntite.length > 0;

  // Ouvre uniquement les dossiers menant à un classeur quand une ou plusieurs
  // entités bâtimentaires (bâtiment/niveau/espace/pièce) sont sélectionnées
  useEffect(() => {
    if (hasSelectedEntite) {
      setExpandedKeys(getKeysExpandedForClasseurs(flatData));
    }
  }, [selectedEntite, mergedData]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = filterTree(flatData, searchQuery);
      setExpandedKeys(getAllKeysExpanded(filtered));
    } else if (
      (!selectedDossiers || selectedDossiers.length === 0) &&
      !hasSelectedEntite &&
      selectedEspaceId == null
    ) {
      setExpandedKeys({});
    }
  }, [searchQuery, mergedData, selectedDossiers, selectedEntite, selectedEspaceId]);

  useEffect(() => {
    if (selectedDossiers && selectedDossiers.length > 0) {
      const filtered = filterByDossiers(flatData, selectedDossiers);
      setExpandedKeys(getAllKeysExpanded(filtered));
    }
  }, [selectedDossiers, mergedData]);

  // Ouvre uniquement les dossiers menant à un classeur rattaché à l'espace
  // sélectionné dans le filtre "Espace"
  useEffect(() => {
    if (selectedEspaceId != null) {
      const filtered = filterByEspace(flatData, selectedEspaceId);
      setExpandedKeys(getAllKeysExpanded(filtered));
    }
  }, [selectedEspaceId, mergedData]);

  useEffect(() => {
    if (!actif) return;

    const processClasseur = (classeur: Classeur) => ({
      label: classeur.nom_classeur,
      created: classeur.created_at,
      isClasseur: true,
      espaces: classeur.espaces, // nécessaire pour le filtre par espace
      children: classeur.fichiers.map((fichier: Fichier) => ({
        key: fichier.id,
        label: fichier.nom_fichier,
        isFichier: true,
        fichierData: fichier,
      })),
    });

    const processChildren = (children, classeurs: Classeur[]) => {
      return children.map((child) => {
        const existingLabels = (child.children || []).map((c) => c.label);

        const matchingClasseurs = classeurs.filter(
          (classeur) =>
            classeur.typologie_gexpertise === child.label &&
            !existingLabels.includes(classeur.nom_classeur)
        );

        const processedClasseurs = matchingClasseurs.map(processClasseur);

        return {
          ...child,
          children: [...(child.children || []), ...processedClasseurs],
        };
      });
    };

    const updateData = (currentData, classeurs: Classeur[]) => {
      return currentData?.map((item) => {
        if (item.children) {
          const updatedChildren = updateData(item.children, classeurs);
          const processedChildren = processChildren(updatedChildren, classeurs);

          return {
            ...item,
            children: processedChildren,
          };
        } else {
          const matchingClasseurs = classeurs.filter(
            (classeur) =>
              classeur.typologie_gexpertise === item.label &&
              !["Rapport de diagnostic", "Fiche récapitulative", "Annexes"].includes(
                classeur.typologie_gexpertise
              )
          );
          const processedChildren = matchingClasseurs.flatMap(processClasseur);

          if (matchingClasseurs.length > 0) {
            return {
              ...item,
              children: processedChildren,
            };
          } else {
            return item;
          }
        }
      });
    };

    const updatedData = updateData(data, actif.classeurs);
    setMergedData(updatedData);
  }, [data, actif]);

  const togglerTemplate = (node, options) => {
    const isSelected = selectedClasseur.some(
      (classeur) => classeur.nom_classeur === node.label && node.isClasseur
    );
    if (!node) {
      return;
    }

    const hasChildren = node.children && node.children.length > 0;
    if (!hasChildren) {
      return null;
    }

    const expanded = options.expanded;
    const iconClassName = classNames("p-tree-toggler-icon pi pi-fw", {
      "pi-caret-right": !expanded,
      "pi-caret-down": expanded,
    });

    return (
      <button type="button" className="p-tree-toggler p-link" tabIndex={-1} onClick={options.onClick}>
        <span className={iconClassName} aria-hidden="true"></span>
      </button>
    );
  };

  const flattenData = (data, parentKey = null) => {
    return data.map((item, index) => {
      const key = parentKey !== null ? parentKey + "-" + index : String(index);
      return {
        key,
        label: item.label,
        isFichier: item.isFichier,
        isClasseur: item.isClasseur,
        fichierData: item.fichierData,
        created: item.created,
        espaces: item.espaces,
        children: item.children ? flattenData(item.children, key) : null,
      };
    });
  };

  var flatData = flattenData(mergedData);
  const [isSelectedList, setIsSelectedList] = useState([]);

  const EntitySelectedbyClasseur = (nom) => {
    const TargetClasseurSelected = actif.classeurs.find((classeur) => classeur.nom_classeur === nom);

    if (TargetClasseurSelected) {
      setSelectedClasseur((prevSelected) =>
        prevSelected.some((classeur) => classeur.nom_classeur === nom)
          ? prevSelected.filter((classeur) => classeur.nom_classeur !== nom)
          : [...prevSelected, TargetClasseurSelected]
      );
    }
  };

  const countFichiers = (node): number => {
    if (!node.children || node.children.length === 0) return 0;
    return node.children.reduce((acc, child) => {
      if (child.isFichier) return acc + 1;
      return acc + countFichiers(child);
    }, 0);
  };

  const renderNode = (node) => {
    const handleClick = () => {
      if (node.isClasseur) {
        EntitySelectedbyClasseur(node.label);
        setIsSelectedList((prevList) =>
          prevList.includes(node.label)
            ? prevList.filter((label) => label !== node.label)
            : [...prevList, node.label]
        );
      }
    };

    if (node.isFichier === true) {
      const margin_left = node.key.split("-").length * 30 + "px";
      const fichierStyle = {
        marginLeft: margin_left,
      };

      return (
        <div className="flex justify-between overflow-auto max-h-48">
          <div
            onClick={() => window.open(node?.fichierData.url_ressources, "_blank")}
            className="cursor-pointer flex space-x-2 items-center"
            style={fichierStyle}
          >
            <img src="/images/fichier.png" className="h-5 w-5" style={{ marginLeft: "-5px" }} />
            <div className="text-left rtl:text-right">
              <h1 className="text-sm font-semibold text-gray-700 normal-case">{node.label}</h1>
              <p className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                {convertKBtoGo(node.fichierData.size)}
                {node.fichierData.created_at && (
                  <span>· {moment(node.fichierData.created_at).format("DD/MM/YYYY")}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      );
    } else {
      const margin_left = node.key.split("-").length * 30 + "px";
      const isSelected = selectedClasseur.some(
        (classeur) => classeur.nom_classeur === node.label && node.isClasseur
      );
      const isInSelectedList = isSelectedList.includes(node.label);
      const fileCount = countFichiers(node);

      const folderStyle: React.CSSProperties = {
        marginLeft: margin_left,
        borderRadius: isSelected ? "4px" : "0px",
        padding: "2px 5px",
        transition: "background-color 0.3s ease",
      };

      return (
        <div className="flex space-x-2 items-center cursor-pointer" style={folderStyle} onClick={handleClick}>
          <img
            src={node.isClasseur ? "/images/reliures.png" : "/images/folder 1.png"}
            className="h-8 w-8"
            style={{ marginLeft: "-5px" }}
          />
          <div className="flex justify-between items-center w-full">
            <div className="text-left rtl:text-right">
              <h1 className="text-md font-semibold text-gray-600 normal-case">{node.label}</h1>
            </div>

            <div className="flex items-center gap-2">
              {fileCount > 0 && !expandedKeys[node.key] && (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "#f3e8ee",
                    color: "#9b1c4a",
                    minWidth: "1.4rem",
                    textAlign: "center",
                  }}
                >
                  {fileCount}
                </span>
              )}
              {isSelected && isInSelectedList && <Chip icon="pi pi-check" />}
            </div>
          </div>
        </div>
      );
    }
  };

  const getsearch = (searchQuery, data) => {
    let results = [];

    for (const node of data) {
      if (node.label && node.label.toLowerCase().includes(searchQuery.toLowerCase())) {
        results.push(node);
      }

      if (node.children && node.children.length > 0) {
        const childResults = getsearch(searchQuery, node.children);
        results = results.concat(childResults);
      }
    }
    return results;
  };

  const getsearchuser = (UserQuery, data) => {
    let results = [];

    for (const node of data) {
      if (node.isFichier && node.fichierData.user_id == UserQuery.id) {
        results.push(node);
      }

      if (node.children && node.children.length > 0) {
        const childResults = getsearchuser(UserQuery, node.children);
        results = results.concat(childResults);
      }
    }
    return results;
  };

  const GetByDates = (start, end, data) => {
    let results = [];
    for (const node of data) {
      if (
        node.isClasseur &&
        moment(node.created).isBetween(moment(start).startOf("day"), moment(end).endOf("day"))
      ) {
        results.push(node);
      }

      if (node.children && node.children.length > 0) {
        const childResults = GetByDates(start, end, node.children);
        results = results.concat(childResults);
      }
    }

    return results;
  };

  const getFilteredData = () => {
    let result = flatData;

    if (searchQuery) {
      result = filterTree(result, searchQuery);
    }
    if (selectedDossiers && selectedDossiers.length > 0) {
      result = filterByDossiers(result, selectedDossiers);
    }
    if (selectedEspaceId != null) {
      result = filterByEspace(result, selectedEspaceId);
    }
    if (UserQuery) {
      result = getsearchuser(UserQuery, result);
    }
    if (selectionRange.startDate != null) {
      result = GetByDates(selectionRange.startDate, selectionRange.endDate, result);
    }

    return result;
  };
  return (
    <>
      <TreeTable
        value={getFilteredData()}
        expandedKeys={expandedKeys}
        onToggle={(e) => setExpandedKeys(e.value)}
        togglerTemplate={togglerTemplate}
        style={{ width: "60rem" }}
        className="overflow-auto "
      >
        <Column expander style={{ width: "2rem" }} />
        <Column field="label" body={renderNode} />
      </TreeTable>
    </>
  );
};

export const ArborescenceFidji = ({
  actif,
  searchQuery,
  UserQuery,
  selectedClasseur,
  setSelectedClasseur,
  selectionRange,
  selectedDossiers,
  selectedEntite,
  selectedEspaceId,
}: ArborescenceProps) => {
  const [expandedKeys, setExpandedKeys] = useState({});
  const [mergedData, setMergedData] = useState([]);
  const [data, setData] = useState([
    {
      key: "1",
      label: "01. Acquisition",
      children: [
        { key: "1-01", label: "01.01. Due diligence" },
        { key: "1-02", label: "01.02. Titres & actes" },
      ],
    },
    {
      key: "2",
      label: "02. Propriétaire",
      children: [
        { key: "2-01", label: "02.01. Juridique" },
        { key: "2-02", label: "02.02. Contrats bancaires (engagements)" },
        { key: "2-03", label: "02.03. Comptabilité" },
        { key: "2-04", label: "02.04. Fiscalité" },
        { key: "2-05", label: "02.05. Contrats tiers" },
      ],
    },
    {
      key: "3",
      label: "03. Présentation de l’actif",
      children: [{ key: "3-01", label: "03.01. Médias" }],
    },
    {
      key: "4",
      label: "04. Construction & Urbanisme",
      children: [
        { key: "4-01", label: "4.01. Permis" },
        { key: "4-02", label: "4.02. Autres autorisations administratives" },
        { key: "4-03", label: "4.03. Contrats & assurances" },
        { key: "4-04", label: "4.04. Contrôles & études" },
        { key: "4-05", label: "4.05. Réception de travaux" },
      ],
    },
    {
      key: "5",
      label: "05. Gestion administrative immeuble",
      children: [
        { key: "5-01", label: "05.01. Plans" },
        { key: "5-02", label: "05.02. Surfaces" },
        { key: "5-03", label: "05.03. Copropriété & gouvernance de voisinage" },
        { key: "5-04", label: "05.04. Mandats" },
        { key: "5-05", label: "05.05. Documents d’urbanisme" },
      ],
    },
    {
      key: "6",
      label: "06. Gestion locative & immobilière",
      children: [
        { key: "6-01", label: "06.01. Situation locative" },
        { key: "6-02", label: "06.02. Locataire" },
        { key: "6-03", label: "06.03. Exploitation" },
        { key: "6-04", label: "06.04. Fiscalité" },
      ],
    },
    {
      key: "7",
      label: "07. Valorisation",
      children: [{ key: "7-01", label: "07.01. Expertises" }],
    },
    {
      key: "8",
      label: "08. Dossier technique",
      children: [
        {
          key: "8-01",
          label: "08.01. Diagnostics techniques & environnementaux",
          children: [
            {
              key: "8-01-01",
              label: "08.01.01. Diagnostics techniques",
              children: [
                { key: "8-01-01-01", label: "Diagnostics amiante" },
                { key: "8-01-01-02", label: "Diagnostics plomb" },
                { key: "8-01-01-03", label: "DPE" },
              ],
            },
            { key: "8-01-05", label: "08.01.05. PV de sécurité incendie et accessibilité" },
            { key: "8-01-02", label: "08.01.02. Audit environnemental +" },
          ],
        },
        { key: "8-02", label: "08.02. Certifications" },
        { key: "8-03", label: "08.03. Autres rapports" },
      ],
    },
    {
      key: "9",
      label: "09. Données économiques",
      children: [
        { key: "9-01", label: "09.01. Commerces" },
        { key: "9-02", label: "09.02. Benchmarking" },
      ],
    },
    {
      key: "10",
      label: "10. Critères ESG",
      children: [
        { key: "10-01", label: "10.01. Documents partagés" },
        { key: "10-02", label: "10.02. Certifications" },
      ],
    },
  ]);

  const filterTree = (data, query) => {
    const lowerQuery = query.toLowerCase();

    const filterNode = (node) => {
      const matchesSelf = node.label && node.label.toLowerCase().includes(lowerQuery);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children.map(filterNode).filter((child) => child !== null);
      }

      const hasMatchingChild = filteredChildren.length > 0;

      if (matchesSelf || hasMatchingChild) {
        return {
          ...node,
          children: matchesSelf ? node.children : filteredChildren,
        };
      }

      return null;
    };

    return data.map(filterNode).filter((node) => node !== null);
  };

  // Filtre par dossier(s) sélectionné(s) : matche le label exact du nœud
  // (ex: "DPE", "Diagnostics amiante") et conserve tout son sous-arbre
  // (classeurs + fichiers).
  const filterByDossiers = (data, dossierNames: string[]) => {
    const filterNode = (node) => {
      const matchesSelf = node.label && dossierNames.includes(node.label);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children.map(filterNode).filter((child) => child !== null);
      }

      const hasMatchingChild = filteredChildren.length > 0;

      if (matchesSelf || hasMatchingChild) {
        return {
          ...node,
          children: matchesSelf ? node.children : filteredChildren,
        };
      }

      return null;
    };

    return data.map(filterNode).filter((node) => node !== null);
  };

  // Filtre par espace sélectionné (sélection unique) : ne garde que les
  // classeurs rattachés à cet espace, et conserve tout leur sous-arbre (fichiers).
  const filterByEspace = (data, espaceId: number) => {
    const filterNode = (node) => {
      const matchesSelf =
        node.isClasseur && node.espaces?.some((espace) => espace.id === espaceId);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children.map(filterNode).filter((child) => child !== null);
      }

      const hasMatchingChild = filteredChildren.length > 0;

      if (matchesSelf || hasMatchingChild) {
        return {
          ...node,
          children: matchesSelf ? node.children : filteredChildren,
        };
      }

      return null;
    };

    return data.map(filterNode).filter((node) => node !== null);
  };

  const getAllKeysExpanded = (nodes, keys = {}) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        keys[node.key] = true;
        getAllKeysExpanded(node.children, keys);
      }
    });
    return keys;
  };

  // Vérifie si un nœud (ou un de ses descendants) est un classeur réel
  const nodeHasClasseur = (node): boolean => {
    if (node.isClasseur) return true;
    if (node.children && node.children.length > 0) {
      return node.children.some(nodeHasClasseur);
    }
    return false;
  };

  // N'ouvre que les branches qui mènent réellement à un classeur
  const getKeysExpandedForClasseurs = (nodes, keys = {}) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0 && nodeHasClasseur(node)) {
        keys[node.key] = true;
        getKeysExpandedForClasseurs(node.children, keys);
      }
    });
    return keys;
  };

  const hasSelectedEntite = selectedEntite && selectedEntite.length > 0;

  // Ouvre uniquement les dossiers menant à un classeur quand une ou plusieurs
  // entités bâtimentaires (bâtiment/niveau/espace/pièce) sont sélectionnées
  useEffect(() => {
    if (hasSelectedEntite) {
      setExpandedKeys(getKeysExpandedForClasseurs(flatData));
    }
  }, [selectedEntite, mergedData]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = filterTree(flatData, searchQuery);
      setExpandedKeys(getAllKeysExpanded(filtered));
    } else if (
      (!selectedDossiers || selectedDossiers.length === 0) &&
      !hasSelectedEntite &&
      selectedEspaceId == null
    ) {
      setExpandedKeys({});
    }
  }, [searchQuery, mergedData, selectedDossiers, selectedEntite, selectedEspaceId]);

  useEffect(() => {
    if (selectedDossiers && selectedDossiers.length > 0) {
      const filtered = filterByDossiers(flatData, selectedDossiers);
      setExpandedKeys(getAllKeysExpanded(filtered));
    }
  }, [selectedDossiers, mergedData]);

  // Ouvre uniquement les dossiers menant à un classeur rattaché à l'espace
  // sélectionné dans le filtre "Espace"
  useEffect(() => {
    if (selectedEspaceId != null) {
      const filtered = filterByEspace(flatData, selectedEspaceId);
      setExpandedKeys(getAllKeysExpanded(filtered));
    }
  }, [selectedEspaceId, mergedData]);

  useEffect(() => {
    if (!actif) return;

    const processClasseur = (classeur: Classeur) => ({
      label: classeur.nom_classeur,
      created: classeur.created_at,
      isClasseur: true,
      espaces: classeur.espaces, // nécessaire pour le filtre par espace
      children: classeur.fichiers.map((fichier: Fichier) => ({
        label: fichier.nom_fichier,
        isFichier: true,
        fichierData: fichier,
      })),
    });

    const processChildren = (children, classeurs: Classeur[]) => {
      return children.map((child) => {
        if (child.children) {
          const processedNestedChildren = processChildren(child.children, classeurs);

          return {
            ...child,
            children: processedNestedChildren,
          };
        } else {
          const matchingClasseurs = classeurs.filter(
            (classeur: Classeur) => classeur.typologie_fidji === child.label
          );

          return matchingClasseurs.length > 0
            ? {
                ...child,
                children: matchingClasseurs.map(processClasseur),
              }
            : child;
        }
      });
    };

    const updateData = (currentData, classeurs: Classeur[]) => {
      return currentData.map((item) => {
        if (item.children) {
          return {
            ...item,
            children: processChildren(item.children, classeurs),
          };
        } else {
          const matchingClasseur = classeurs.find(
            (classeur) => classeur.typologie_fidji === item.label
          );

          return matchingClasseur
            ? {
                ...item,
                children: [processClasseur(matchingClasseur)],
              }
            : item;
        }
      });
    };

    const updatedData = updateData(data, actif.classeurs);
    setMergedData(updatedData);
  }, [data, actif]);

  const togglerTemplate = (node, options) => {
    const isSelected = selectedClasseur.some(
      (classeur) => classeur.nom_classeur === node.label && node.isClasseur
    );
    if (!node) {
      return;
    }

    const hasChildren = node.children && node.children.length > 0;
    if (!hasChildren) {
      return null;
    }

    const expanded = options.expanded;
    const iconClassName = classNames("p-tree-toggler-icon pi pi-fw", {
      "pi-caret-right": !expanded,
      "pi-caret-down": expanded,
    });

    return (
      <button type="button" className="p-tree-toggler p-link" tabIndex={-1} onClick={options.onClick}>
        <span className={iconClassName} aria-hidden="true"></span>
      </button>
    );
  };

  const flattenData = (data, parentKey = null) => {
    return data.map((item, index) => {
      const key = parentKey !== null ? parentKey + "-" + index : String(index);
      return {
        key,
        label: item.label,
        isFichier: item.isFichier,
        isClasseur: item.isClasseur,
        fichierData: item.fichierData,
        created: item.created,
        espaces: item.espaces,
        children: item.children ? flattenData(item.children, key) : null,
      };
    });
  };

  const flatData = flattenData(mergedData);
  const [isSelectedList, setIsSelectedList] = useState([]);

  const EntitySelectedbyClasseur = (nom) => {
    const TargetClasseurSelected = actif.classeurs.find((classeur) => classeur.nom_classeur === nom);

    if (TargetClasseurSelected) {
      setSelectedClasseur((prevSelected) =>
        prevSelected.some((classeur) => classeur.nom_classeur === nom)
          ? prevSelected.filter((classeur) => classeur.nom_classeur !== nom)
          : [...prevSelected, TargetClasseurSelected]
      );
    }
  };

  const countFichiers = (node): number => {
    if (!node.children || node.children.length === 0) return 0;
    return node.children.reduce((acc, child) => {
      if (child.isFichier) return acc + 1;
      return acc + countFichiers(child);
    }, 0);
  };

  const renderNode = (node) => {
    const handleClick = () => {
      if (node.isClasseur) {
        EntitySelectedbyClasseur(node.label);
        setIsSelectedList((prevList) =>
          prevList.includes(node.label)
            ? prevList.filter((label) => label !== node.label)
            : [...prevList, node.label]
        );
      }
    };

    if (node.isFichier === true) {
      const margin_left = node.key.split("-").length * 30 + "px";
      const fichierStyle = {
        marginLeft: margin_left,
      };

      return (
        <div className="flex justify-between overflow-auto max-h-48">
          <div
            onClick={() => window.open(node?.fichierData.url_ressources, "_blank")}
            className="cursor-pointer flex space-x-2 items-center"
            style={fichierStyle}
          >
            <img src="/images/fichier.png" className="h-5 w-5" style={{ marginLeft: "-5px" }} />
            <div className="text-left rtl:text-right">
              <h1 className="text-sm font-semibold text-gray-700 normal-case">{node.label}</h1>
              <p className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                {convertKBtoGo(node.fichierData.size)}
                {node.fichierData.created_at && (
                  <span>· {moment(node.fichierData.created_at).format("DD/MM/YYYY")}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      );
    } else {
      const margin_left = node.key.split("-").length * 30 + "px";
      const isSelected = selectedClasseur.some(
        (classeur) => classeur.nom_classeur === node.label && node.isClasseur
      );
      const isInSelectedList = isSelectedList.includes(node.label);
      const fileCount = countFichiers(node);

      const folderStyle: React.CSSProperties = {
        marginLeft: margin_left,
        borderRadius: isSelected ? "4px" : "0px",
        padding: "2px 5px",
        transition: "background-color 0.3s ease",
      };

      return (
        <div className="flex space-x-2 items-center cursor-pointer" style={folderStyle} onClick={handleClick}>
          <img
            src={node.isClasseur ? "/images/reliures.png" : "/images/folder 1.png"}
            className="h-8 w-8"
            style={{ marginLeft: "-5px" }}
          />
          <div className="flex justify-between items-center w-full">
            <div className="text-left rtl:text-right">
              <h1 className="text-md font-semibold text-gray-600 normal-case">{node.label}</h1>
            </div>

            <div className="flex items-center gap-2">
              {fileCount > 0 && !expandedKeys[node.key] && (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "#f3e8ee",
                    color: "#9b1c4a",
                    minWidth: "1.4rem",
                    textAlign: "center",
                  }}
                >
                  {fileCount}
                </span>
              )}
              {isSelected && isInSelectedList && <Chip icon="pi pi-check" />}
            </div>
          </div>
        </div>
      );
    }
  };

  const getsearch = (searchQuery, data) => {
    let results = [];
    for (const node of data) {
      if (node.label && node.label.toLowerCase().includes(searchQuery.toLowerCase())) {
        results.push(node);
      }
      if (node.children && node.children.length > 0) {
        const childResults = getsearch(searchQuery, node.children);
        results = results.concat(childResults);
      }
    }
    return results;
  };

  const getsearchuser = (UserQuery, data) => {
    let results = [];
    for (const node of data) {
      if (node.isFichier && node.fichierData.user_id == UserQuery.id) {
        results.push(node);
      }
      if (node.children && node.children.length > 0) {
        const childResults = getsearchuser(UserQuery, node.children);
        results = results.concat(childResults);
      }
    }
    return results;
  };

  const GetByDates = (start, end, data) => {
    let results = [];

    for (const node of data) {
      if (
        node.isClasseur &&
        moment(node.created).isBetween(moment(start).startOf("day"), moment(end).endOf("day"))
      ) {
        results.push(node);
      }

      if (node.children && node.children.length > 0) {
        const childResults = GetByDates(start, end, node.children);
        results = results.concat(childResults);
      }
    }

    return results;
  };

  const getFilteredData = () => {
    let result = flatData;

    if (searchQuery) {
      result = filterTree(result, searchQuery);
    }
    if (selectedDossiers && selectedDossiers.length > 0) {
      result = filterByDossiers(result, selectedDossiers);
    }
    if (selectedEspaceId != null) {
      result = filterByEspace(result, selectedEspaceId);
    }
    if (UserQuery) {
      result = getsearchuser(UserQuery, result);
    }
    if (selectionRange.startDate != null) {
      result = GetByDates(selectionRange.startDate, selectionRange.endDate, result);
    }

    return result;
  };

  return (
    <>
      <TreeTable
        value={getFilteredData()}
        expandedKeys={expandedKeys}
        onToggle={(e) => setExpandedKeys(e.value)}
        togglerTemplate={togglerTemplate}
        style={{ width: "60rem", maxHeight: "90rem" }}
        className="overflow-auto "
      >
        <Column expander style={{ width: "2rem" }} />
        <Column field="label" body={renderNode} />
      </TreeTable>
    </>
  );
};